import "@/App.css";
import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import CustomerLayout from "./components/CustomerLayout";
import Login from "./pages/customer/Login";
import Register from "./pages/customer/Register";
import ForgotPassword from "./pages/customer/ForgotPassword";
import ResetPassword from "./pages/customer/ResetPassword";
import CompleteProfile from "./pages/customer/CompleteProfile";
import Home from "./pages/customer/Home";
import Cart from "./pages/customer/Cart";
import Orders from "./pages/customer/Orders";
import Profile from "./pages/customer/Profile";
import AdminApp from "./pages/admin/AdminApp";

function Protected({ children }) {
  const { isAuthenticated, loading, sessionExpired } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!isAuthenticated) {
    const to = sessionExpired ? `/login?expired=1&next=${encodeURIComponent(location.pathname)}` : "/login";
    return <Navigate to={to} replace />;
  }
  return children;
}

function RootRedirect() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={isAuthenticated ? "/home" : "/login"} replace />;
}

function AuthCallback() {
  const { googleSession } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) {
      navigate("/login", { replace: true });
      return;
    }
    googleSession(sid)
      .then(({ needs_profile }) => {
        window.history.replaceState(null, "", window.location.pathname);
        navigate(needs_profile ? "/complete-profile" : "/home", { replace: true });
      })
      .catch(() => {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/login?google=failed", { replace: true });
      });
  }, [googleSession, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FCFBF8] gap-4">
      <Loader2 className="animate-spin text-[#228B22]" size={36} />
      <p className="text-gray-500 text-sm">Signing you in…</p>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Process the Google OAuth callback BEFORE any protected-route guard runs.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/complete-profile" element={<Protected><CompleteProfile /></Protected>} />
      <Route
        element={
          <Protected>
            <CustomerLayout />
          </Protected>
        }
      >
        <Route path="/home" element={<Home />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/profile" element={<Profile />} />
      </Route>
      <Route path="/admin/*" element={<AdminApp />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <Toaster position="top-center" richColors />
            <AppRoutes />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
